
import { PARegistry } from "../singletons/pa-registry";
import type { TravelTechnicianDateService } from "src/app/_services/travel_technician_date.service";
import type { CalendarWeek } from "src/app/_models/planning-assistant.interface";
import { PAUtil } from "./util";
import { PACoordinates } from "./coordinates";
import type { TravelTechnicianDate as TravelTechnicianDateHash } from "src/app/_models/planning-assistant.interface";
import { PALocation } from "./location";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";

export class TravelTechnicianDate extends FireUpdateOnChange<void>{

  // Zentrale Verwaltung der Reise-Tage — hierher gezogen aus PATechnicianDate, damit
  // travel-technician-date keinen Wert-Import-Zyklus mit technician-date bildet
  static travelTechnicianDates = new Map<number, Map<number, TravelTechnicianDate> | 'loading'>()
  static travelTechnicianDateService: TravelTechnicianDateService

  static setTravelTechnicianDateService(service: TravelTechnicianDateService): void {
    this.travelTechnicianDateService = service
  }

  static async getTravelTechnicianDate(uid: number, timestamp: number): Promise<TravelTechnicianDate> {
    if (!TravelTechnicianDate.travelTechnicianDates.has(timestamp)) {
      const cw = PARegistry.timeControl.timestampToCalendarWeek(timestamp)
      await this.loadTravelTechnicianDatesForCalendarWeek(cw)
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return TravelTechnicianDate.travelTechnicianDates.get(timestamp) == 'loading'
      }
    )

    const travel_technician_date_user_map = TravelTechnicianDate.travelTechnicianDates.get(timestamp) as Map<number, TravelTechnicianDate>
    if (!travel_technician_date_user_map.has(uid)) {
      travel_technician_date_user_map.set(uid, new TravelTechnicianDate(uid, timestamp))
    }
    return travel_technician_date_user_map.get(uid)
  }

  static async loadTravelTechnicianDatesForCalendarWeek(cw: CalendarWeek): Promise<void> {
    const cwd = PARegistry.timeControl.getCalendarWeekData(cw)
    const ttds = TravelTechnicianDate.travelTechnicianDates
    ttds.set(cwd.weekdays.monday.timestamp, 'loading')
    ttds.set(cwd.weekdays.tuesday.timestamp, 'loading')
    ttds.set(cwd.weekdays.wednesday.timestamp, 'loading')
    ttds.set(cwd.weekdays.thursday.timestamp, 'loading')
    ttds.set(cwd.weekdays.friday.timestamp, 'loading')
    ttds.set(cwd.weekdays.saturday.timestamp, 'loading')
    ttds.set(cwd.weekdays.sunday.timestamp, 'loading')

    const date_from = PARegistry.timeControl.dateToDatestring(new Date(cwd.weekdays['monday'].timestamp), true, false) + " 00:00:00"
    const date_until = PARegistry.timeControl.dateToDatestring(new Date(cwd.weekdays['sunday'].timestamp), true, false) + " 23:59:59"
    return new Promise((resolve, reject) => {
        this.travelTechnicianDateService.getTravelTechnicianDates(date_from, date_until).subscribe(
          async (data) => {
            ttds.set(cwd.weekdays.monday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.tuesday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.wednesday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.thursday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.friday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.saturday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.sunday.timestamp, new Map<number, TravelTechnicianDate>())

            for (const ttd_hash of data) {
              const lat = ttd_hash.location_address_latitude
              const lng = ttd_hash.location_address_longitude
              const coordinates = lat && lng ? new PACoordinates(lat, lng, ttd_hash.location_name) : null
              const timestamp = PARegistry.timeControl.getUTCDayTimestampOfLocalDateString(ttd_hash.date)
              const ttd = new TravelTechnicianDate(ttd_hash.user_id, timestamp, {
                dbID: ttd_hash.id,
                coordinates: coordinates
              })
              const map = ttds.get(timestamp) as Map<number, TravelTechnicianDate>
              map.set(ttd_hash.user_id, ttd)
            }

            resolve()
          },
          (err) => {
            console.error(err)
            reject()
          },
        )
      }
    )
  }



  executeBeforeChange(): void {
  }

  private _dbID?: number
  public location?: PALocation
  private _lastSavedCoordinates: PACoordinates

  constructor(
    readonly technicianID: number,
    readonly timestamp: number,
    config?: { dbID?: number, coordinates?: PACoordinates }
  ) {
    super()
    if (config?.dbID) {
      this._dbID = config?.dbID
      this._lastSavedCoordinates = config?.coordinates
    }
    this.initLocation(config?.coordinates)
  }

  get coordinates() {
    return this.location?.coordinates
  }

  set coordinates(coordinates: PACoordinates) {
    if (!this.location) {
      this.initLocation(coordinates)
    } else {
      this.setLocationCoordinates(coordinates)
    }
  }

  initLocation(coordinates: PACoordinates): void {
    if (!this.location && coordinates) {
      this.setLocationCoordinates(coordinates)
    }
  }

  setLocationCoordinates(coordinates: PACoordinates): void {
    this.location = new PALocation(coordinates, PARegistry.dataControl.distanceCalculator, PARegistry.dataControl.generateNextLocationId(), '', '', '')
  }

  isActive(): boolean {
    return !!this._dbID
  }

  async updateInDB(): Promise<TravelTechnicianDateHash> {
    if (this.isActive()) {
      const travel_technician_date_data = {
        location_name: this.coordinates?.name || null,
        location_address_latitude: this.coordinates?.latitude || null,
        location_address_longitude: this.coordinates?.longitude || null
      }

      return new Promise((resolve) => {
        TravelTechnicianDate.travelTechnicianDateService.updateTravelTechnicianDate(this._dbID, {travel_technician_date: travel_technician_date_data}).subscribe(
          async (data) => {
            const map = TravelTechnicianDate.travelTechnicianDates.get(this.timestamp) as Map<number, TravelTechnicianDate>
            map.set(this.technicianID, this)
            resolve(data)
          },
          (err) => {
            console.error(err)
          },
        )
      })
    }
  }

  async createInDB(): Promise<TravelTechnicianDateHash> {
    if (!this.isActive()) {
      let travel_technician_date_data: {
        user_id: number,
        date: string,
        location_name?: string,
        location_address_latitude?: number,
        location_address_longitude?: number
      }
      if (this.coordinates) {
        travel_technician_date_data = {
          user_id: this.technicianID,
          date: PARegistry.timeControl.formatDate(new Date(this.timestamp)),
          location_name: this.coordinates?.name,
          location_address_latitude: this.coordinates?.latitude,
          location_address_longitude: this.coordinates?.longitude
        }
      } else {
        travel_technician_date_data = {
          user_id: this.technicianID,
          date: PARegistry.timeControl.formatDate(new Date(this.timestamp))
        }
      }

      return new Promise((resolve) => {
        TravelTechnicianDate.travelTechnicianDateService.createTravelTechnicianDate(travel_technician_date_data).subscribe(
          async (data) => {
            this._dbID = data.id
            if (data.location_address_latitude && data.location_address_longitude) {
              this._lastSavedCoordinates = new PACoordinates(data.location_address_latitude, data.location_address_longitude, data.location_name)
            }
            resolve(data)
          },
          (err) => {
            console.error(err)
          },
        )
      })
    }
  }

  async deleteInDb(): Promise<boolean> {
    if (this.isActive()) {
      return new Promise((resolve) => {
          TravelTechnicianDate.travelTechnicianDateService.deleteTravelTechnicianDate(this._dbID).subscribe(
            async _ => {
              this._dbID = null
              this.coordinates = null
              resolve(true)
            },
            (err) => {
              console.error(err)
              resolve(false)
            },
          )
        }
      )
    }
  }

  hasChanged(): void {
    this.fireUpdate()
  }

}
